import { fromEvent, Observable, merge, race } from 'rxjs'
import { takeUntil, skipWhile, map, debounceTime } from 'rxjs/operators'
import { Stream } from 'agora-rtc-sdk'

type AudioAnalyserParams = {
  smoothingTimeConstant: number
  fftSize: number
}

const MAX_VOLUME_DB_LEVEL = 255

const AUDIO_ANALYSER_PARAMS_DEFAULTS: AudioAnalyserParams = {
  smoothingTimeConstant: 0.3,
  fftSize: 1024,
}

export function getAudioDeviceIdByStream (stream: Stream): string | null {
  const track = stream.getAudioTrack() as MediaStreamTrack
  return track?.getCapabilities().deviceId ?? null
}

export function createAudioCtx () {
  try {
    return new AudioContext()
  } catch (e) {
    return null
  }
}

function getAverageVolume (frequenciesAmplitudesDbArray: Uint8Array): number {
  let frequenciesAmplitudesSum = 0
  const frequenciesCount = frequenciesAmplitudesDbArray.length

  // get all the frequency amplitudes
  for (let i = 0; i < frequenciesCount; i++) {
    const freqAmplitude = frequenciesAmplitudesDbArray[i]
    if (Number.isFinite(freqAmplitude)) {
      frequenciesAmplitudesSum += freqAmplitude
    }
  }
  return frequenciesAmplitudesSum / frequenciesCount
}

function isMeadiaStreamTrackActive (mediaStreamTrack: MediaStreamTrack): boolean {
  return mediaStreamTrack.enabled && mediaStreamTrack.readyState === 'live'
}

function isAudioStreamActive (audioStream: MediaStream): boolean {
  if (!audioStream.active) {
    return false
  }
  const streamTracks = audioStream.getAudioTracks()

  return streamTracks.length > 0 && streamTracks.some(isMeadiaStreamTrackActive)
}

function createAudioStreamFinishedRxSource (
  audioStream: MediaStream
): Observable<Event> {
  const audioStreamInactiveRxSource = fromEvent(audioStream, 'inactive')
  const audioStreamTrackRemovedRxSource = fromEvent(audioStream, 'removetrack')
  const audioStreamTrackAddedRxSource = fromEvent(audioStream, 'addtrack')
  const audioStreamFinishedRxSource = merge(
    audioStreamTrackRemovedRxSource,
    audioStreamTrackAddedRxSource,
    audioStreamInactiveRxSource
  ).pipe(
    debounceTime(1000),
    skipWhile(() => isAudioStreamActive(audioStream))
  )
  return audioStreamFinishedRxSource
}

export function getVolumeLevelObservable (
  audioCtx: AudioContext,
  audioStream: MediaStream,
  audioAnalyzerParams: AudioAnalyserParams = AUDIO_ANALYSER_PARAMS_DEFAULTS,
): Observable<number> {
  if (!isAudioStreamActive(audioStream)) {
    throw new Error('Audio stream must be active')
  }

  const analyser = audioCtx.createAnalyser()
  const source = audioCtx.createMediaStreamSource(audioStream)
  // if channelCountMode is "max" the channelCount is ignored
  const channelsCount =
    source.channelCountMode !== 'max' ? source.channelCount : undefined
  const scriptProcessor = audioCtx.createScriptProcessor(
    audioAnalyzerParams.fftSize * 2, // just as in the example
    channelsCount,
    1
  )

  const audioStreamFinishedRxSource = createAudioStreamFinishedRxSource(
    audioStream
  )
  const scriptProcessorAudioProcessedRxSource = fromEvent(
    scriptProcessor,
    'audioprocess'
  )

  Object.assign(analyser, audioAnalyzerParams)

  source.connect(analyser)
  analyser.connect(scriptProcessor)
  scriptProcessor.connect(audioCtx.destination)

  const mickVolumeCheckRxSource = scriptProcessorAudioProcessedRxSource.pipe(
    takeUntil(audioStreamFinishedRxSource),
    map((): number => {
      const frequenciesAmplitudesDbArray = new Uint8Array(
        analyser.frequencyBinCount
      )

      void analyser.getByteFrequencyData(frequenciesAmplitudesDbArray)
      return getAverageVolume(frequenciesAmplitudesDbArray) / MAX_VOLUME_DB_LEVEL
    })
  )

  function handleObservableComplete () {
    scriptProcessor.disconnect()
    analyser.disconnect()
    source.disconnect()
  }

  mickVolumeCheckRxSource.subscribe({
    error: handleObservableComplete,
    complete: handleObservableComplete,
  })
  return mickVolumeCheckRxSource
}

export function createIsMediaStreamTrackActiveObservable (mediaStreamTrack: MediaStreamTrack): Observable<boolean> {
  const trackEndedRxSource = fromEvent(mediaStreamTrack, 'ended')
  const trackNotPermittedRxSource = fromEvent(mediaStreamTrack, 'isolationchange')
  const trackUnmutedRxSource = fromEvent(mediaStreamTrack, 'unmute')
  const emitFirstTimeSource = new Observable((observer) => observer.next())

  function returnWhetherStreamTrackActive (): boolean {
    return isMeadiaStreamTrackActive(mediaStreamTrack)
  }

  return race(trackNotPermittedRxSource, trackUnmutedRxSource, emitFirstTimeSource)
    .pipe(takeUntil(trackEndedRxSource))
    .pipe(map(returnWhetherStreamTrackActive))
}
